import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { api, type AnnouncementReader } from '../../lib/api';
import { useDepartmentName } from '../../store/departmentStore';

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Admins: who has confirmed reading an announcement, with a CSV export. */
export function ReadReportDialog({ announcementId, onClose }: { announcementId: string | null; onClose: () => void }) {
  const departmentName = useDepartmentName();
  const [title, setTitle] = useState('');
  const [people, setPeople] = useState<AnnouncementReader[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'pending' | 'read'>('pending');

  const load = () => {
    if (!announcementId) return;
    setPeople(null);
    setError(null);
    setTab('pending');
    api.announcements
      .reads(announcementId)
      .then((r) => {
        setTitle(r.title);
        setPeople(r.people);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the report'));
  };

  const read = (people ?? []).filter((p) => p.read_at);
  const pending = (people ?? []).filter((p) => !p.read_at);
  const shown = tab === 'read' ? read : pending;

  const exportCsv = () => {
    const rows = [['Name', 'Email', 'Department', 'Confirmed at']].concat(
      (people ?? []).map((p) => [p.name, p.email, p.department_id ? departmentName(p.department_id) : '', p.read_at ?? '']),
    );
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `read-confirmations-${title.replace(/[^\w]+/g, '-').slice(0, 40)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog
      open={Boolean(announcementId)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="read-report-title"
      slotProps={{ transition: { onEnter: load } }}
    >
      <DialogTitle id="read-report-title" component="h2" variant="h6">
        Read confirmations
        <Typography variant="caption" component="div" noWrap>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {people === null && !error ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={22} aria-label="Loading report" />
          </Box>
        ) : (
          people && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {read.length} of {people.length} active users confirmed ({people.length ? Math.round((read.length / people.length) * 100) : 0}%).
              </Typography>
              <Tabs value={tab} onChange={(_, v: 'pending' | 'read') => setTab(v)} sx={{ minHeight: 36, mb: 1 }}>
                <Tab value="pending" label={`Not yet (${pending.length})`} sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab value="read" label={`Confirmed (${read.length})`} sx={{ minHeight: 36, textTransform: 'none' }} />
              </Tabs>
              <Stack sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {shown.map((p) => (
                  <Stack key={p.id} direction="row" spacing={1} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" noWrap component="div">
                        {p.email}
                        {p.department_id ? ` · ${departmentName(p.department_id)}` : ''}
                      </Typography>
                    </Box>
                    {p.read_at && (
                      <Typography variant="caption">{new Date(p.read_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</Typography>
                    )}
                  </Stack>
                ))}
                {!shown.length && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {tab === 'pending' ? 'Everyone has confirmed.' : 'Nobody has confirmed yet.'}
                  </Typography>
                )}
              </Stack>
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" disabled={!people?.length} onClick={exportCsv}>
          Export CSV
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
